import rison from 'rison';
import * as Hooks from 'preact/hooks';

/**
 * Returns a caching version of the async callback that reuses previous
 * results for given args. This has the same semantics for memoisation
 * of the callback as the native `useCallback` hook, and additionally
 * resets its cache upon changes to inputs to avoid caching an older
 * version of the callback.
 * Args to the callback must be serialisable, so some kinds of values
 * (e.g functions, cyclic objects) must not be used in callback args.
 */
export function useCachingAsyncCallback<Args extends any[], Ret>(
    callback: (...args: Args) => PromiseLike<Ret>,
    inputs: Array<unknown>,
): (...args: Args) => Promise<Ret> {
    const cache = Hooks.useRef<Map<string, Ret>>(new Map());

    Hooks.useEffect(() => {
        cache.current = new Map();
    }, inputs);

    const cachedCallback = Hooks.useMemo(
        () =>
            async (...args: Args) => {
                const currentCache = cache.current;

                const argsKey = rison.encode_array(args);

                if (!currentCache.has(argsKey)) {
                    currentCache.set(argsKey, await callback(...args));
                }

                return currentCache.get(argsKey) as Ret;
            },
        inputs,
    );

    return cachedCallback;
}
